import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Student, Guardian } from '@/types';
import { GUARDIAN_RELATIONSHIPS } from '../../../constants';

interface GuardianSectionProps {
  values: Partial<Student>;
  onChange: (id: keyof Student, value: any) => void;
}

export const GuardianSection: React.FC<GuardianSectionProps> = ({
  values,
  onChange
}) => {
  const guardians = values.guardians || [];

  const handleAddGuardian = () => {
    const newGuardian: Guardian = {
      id: `guardian-${Date.now()}`,
      name: '',
      relationship: '',
      phone: '',
      email: '',
      isPrimary: guardians.length === 0
    };
    onChange('guardians', [...guardians, newGuardian]);
  };

  const handleRemoveGuardian = (id: string) => {
    const updatedGuardians = guardians.filter(g => g.id !== id);
    if (updatedGuardians.length > 0 && !updatedGuardians.some(g => g.isPrimary)) {
      updatedGuardians[0].isPrimary = true;
    }
    onChange('guardians', updatedGuardians);
  };

  const handleGuardianChange = (id: string, field: keyof Guardian, value: any) => {
    const updatedGuardians = guardians.map(guardian => {
      if (guardian.id === id) {
        return { ...guardian, [field]: value };
      }
      if (field === 'isPrimary' && value === true) {
        return { ...guardian, isPrimary: false };
      }
      return guardian;
    });
    onChange('guardians', updatedGuardians);
  };

  return (
    <Box>
      {guardians.map((guardian, index) => (
        <Paper 
          key={guardian.id} 
          elevation={0}
          sx={{ 
            p: 2, 
            mb: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ flex: 1 }}>
              {guardian.isPrimary ? 'Primary Guardian' : `Additional Guardian ${index}`}
            </Typography>
            {!guardian.isPrimary && (
              <IconButton 
                onClick={() => handleRemoveGuardian(guardian.id)}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={guardian.name}
                onChange={(e) => handleGuardianChange(guardian.id, 'name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={guardian.relationship}
                  label="Relationship"
                  onChange={(e) => handleGuardianChange(guardian.id, 'relationship', e.target.value)}
                >
                  {GUARDIAN_RELATIONSHIPS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={guardian.phone}
                onChange={(e) => handleGuardianChange(guardian.id, 'phone', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={guardian.email}
                onChange={(e) => handleGuardianChange(guardian.id, 'email', e.target.value)}
                required
              />
            </Grid>

            {!guardian.isPrimary && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleGuardianChange(guardian.id, 'isPrimary', true)}
                >
                  Make Primary Guardian
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddGuardian}
        sx={{ mt: 2 }}
      >
        Add Guardian
      </Button>
    </Box>
  );
};